import type { PortfolioBookKindV2, PortfolioV2SchemaVersion } from "./primitives.js";

export const STATEMENT_IMPORT_FORMATS_V2 = ["CSV", "OFX", "QFX", "PDF", "IMAGE"] as const;
export type StatementImportFormatV2 = typeof STATEMENT_IMPORT_FORMATS_V2[number];
export const STATEMENT_IMPORT_SEMANTIC_ROLES_V2 = ["ACTIVITY_DELTA", "ABSOLUTE_STATE", "DESCRIPTIVE"] as const;
export type StatementImportSemanticRoleV2 = typeof STATEMENT_IMPORT_SEMANTIC_ROLES_V2[number];
export const STATEMENT_IMPORT_EXTRACTORS_V2 = ["STRUCTURED", "PDF_TEXT", "OCR", "OWNER_CORRECTION"] as const;
export type StatementImportExtractorV2 = typeof STATEMENT_IMPORT_EXTRACTORS_V2[number];
export const STATEMENT_IMPORT_FILE_EXTRACTION_MODES_V2 = ["STRUCTURED", "PDF_TEXT", "OCR", "MIXED"] as const;
export type StatementImportFileExtractionModeV2 = typeof STATEMENT_IMPORT_FILE_EXTRACTION_MODES_V2[number];
export const STATEMENT_IMPORT_REVIEW_STATES_V2 = ["UNREVIEWED", "CONFIRMED", "CORRECTED", "REJECTED"] as const;
export type StatementImportReviewStateV2 = typeof STATEMENT_IMPORT_REVIEW_STATES_V2[number];
export const STATEMENT_IMPORT_VALUE_STATES_V2 = ["OBSERVED", "UNKNOWN"] as const;
export type StatementImportValueStateV2 = typeof STATEMENT_IMPORT_VALUE_STATES_V2[number];
export const STATEMENT_IMPORT_RECORD_DISPOSITIONS_V2 = [
  "NEW", "BIND_EXISTING", "SNAPSHOT_ONLY", "POSSIBLE_DUPLICATE", "CONFLICT", "IGNORE",
] as const;
export type StatementImportRecordDispositionV2 = typeof STATEMENT_IMPORT_RECORD_DISPOSITIONS_V2[number];
export const STATEMENT_IMPORT_STATES_V2 = [
  "UPLOADED", "EXTRACTING", "REVIEW_REQUIRED", "RECONCILING", "READY_TO_CONFIRM", "INTENT_CREATED",
  "OBSERVATIONS_ACCEPTED", "DUPLICATE_NOOP", "BLOCKED", "FAILED", "ABANDONED",
] as const;
export type StatementImportStateV2 = typeof STATEMENT_IMPORT_STATES_V2[number];
export const STATEMENT_IMPORT_RECORD_KINDS_V2 = [
  "TRADE_ACTIVITY", "CASH_ACTIVITY", "POSITION_SNAPSHOT", "CASH_SNAPSHOT", "FEE", "INCOME", "OTHER",
] as const;
export type StatementImportRecordKindV2 = typeof STATEMENT_IMPORT_RECORD_KINDS_V2[number];
export const STATEMENT_IMPORT_ACCOUNT_IDENTITY_STATES_V2 = ["RESOLVED", "UNKNOWN", "CONFLICT"] as const;
export const STATEMENT_IMPORT_INSTRUMENT_IDENTITY_STATES_V2 = ["RESOLVED", "UNKNOWN", "AMBIGUOUS"] as const;
export const STATEMENT_IMPORT_EVENT_IDENTITY_STRENGTHS_V2 = ["STRONG", "WEAK", "MISSING", "CONFLICT"] as const;
export const STATEMENT_IMPORT_RECONCILIATION_STATES_V2 = ["NOT_RUN", "BLOCKED", "MATCHED", "CONFLICTED"] as const;
export const STATEMENT_IMPORT_OPERATIONS_V2 = [
  "UPLOAD", "EXTRACTION", "REVIEW", "RECONCILIATION", "ACCEPT_INTENT", "ACCEPT_CONFIRM", "DISCARD",
] as const;
export const STATEMENT_IMPORT_OPERATION_STATES_V2 = [
  "NOT_FOUND", "PENDING", "COMMITTED", "CONFLICT", "ABANDONED", "OUTCOME_UNKNOWN",
] as const;
export const STATEMENT_IMPORT_RECEIPT_STATES_V2 = ["OBSERVATIONS_ACCEPTED", "DUPLICATE_NOOP"] as const;
export const STATEMENT_IMPORT_FIELD_NAMES_V2 = [
  "UNCLASSIFIED", "PROVIDER", "ACCOUNT_REF", "STATEMENT_ID", "PERIOD_START", "PERIOD_END", "AS_OF", "BROKER_INSTRUMENT_ID",
  "ISIN", "CUSIP", "FIGI", "SYMBOL", "EXCHANGE", "CURRENCY", "EXTERNAL_EVENT_ID", "ORDER_ID",
  "EXECUTION_ID", "SIDE", "QUANTITY", "POSITION_QUANTITY", "PRICE", "COST_BASIS", "COMMISSION",
  "OTHER_FEES", "AMOUNT", "CASH_BALANCE", "OCCURRED_AT", "DESCRIPTION",
  "ORDER_TYPE", "LIMIT_PRICE", "STOP_PRICE", "STOP_LOSS_PRICE", "TAKE_PROFIT_PRICE", "STATUS", "INSTRUCTIONS",
  "PLACED_AT", "EXPIRATION_AT", "LEVEL_ID", "LEVERAGE", "MARGIN", "LAST_PRICE",
  "POSITION_VALUE", "MARKET_VALUE", "UNREALIZED_PNL", "UNREALIZED_PNL_PCT", "REALIZED_PNL",
  "BALANCE_BEFORE",
] as const;
export type StatementImportFieldNameV2 = typeof STATEMENT_IMPORT_FIELD_NAMES_V2[number];

export const STATEMENT_IMPORT_ERROR_CODES_V2 = [
  "STATEMENT_IMPORT_REQUEST_INVALID", "STATEMENT_IMPORT_FILE_REQUIRED", "STATEMENT_IMPORT_FILE_EMPTY",
  "STATEMENT_IMPORT_FILE_COUNT_INVALID", "STATEMENT_IMPORT_FILE_TOO_LARGE", "STATEMENT_IMPORT_FORMAT_UNSUPPORTED",
  "STATEMENT_IMPORT_SIGNATURE_INVALID",
  "STATEMENT_IMPORT_PASSWORD_PROTECTED", "STATEMENT_IMPORT_FILE_CORRUPT", "STATEMENT_IMPORT_EXTRACTOR_UNAVAILABLE",
  "STATEMENT_IMPORT_OCR_REQUIRED", "STATEMENT_IMPORT_EVIDENCE_REQUIRED", "STATEMENT_IMPORT_FIELD_UNKNOWN",
  "STATEMENT_IMPORT_ACCOUNT_IDENTITY_REQUIRED", "STATEMENT_IMPORT_ACCOUNT_MISMATCH",
  "STATEMENT_IMPORT_PERIOD_REQUIRED", "STATEMENT_IMPORT_INSTRUMENT_UNRESOLVED",
  "STATEMENT_IMPORT_POSSIBLE_DUPLICATE", "STATEMENT_IMPORT_EXTERNAL_ID_CONFLICT",
  "STATEMENT_IMPORT_ACTIVITY_MISSING", "STATEMENT_IMPORT_REVIEW_REQUIRED",
  "STATEMENT_IMPORT_RECONCILIATION_REQUIRED", "STATEMENT_IMPORT_RECONCILIATION_MISMATCH",
  "STATEMENT_IMPORT_INTENT_NOT_FOUND", "STATEMENT_IMPORT_INTENT_EXPIRED", "STATEMENT_IMPORT_INTENT_MISMATCH",
  "STATEMENT_IMPORT_CONFIRMATION_REQUIRED", "STATEMENT_IMPORT_BOOK_NOT_FOUND", "STATEMENT_IMPORT_BOOK_NOT_ACTIVE",
  "STATEMENT_IMPORT_BOOK_VERSION_CONFLICT", "STATEMENT_IMPORT_STEP_UP_REQUIRED",
  "STATEMENT_IMPORT_IDEMPOTENCY_CONFLICT", "STATEMENT_IMPORT_OUTCOME_UNKNOWN",
  "STATEMENT_IMPORT_RESPONSE_INVALID", "STATEMENT_IMPORT_FINANCIAL_AUTHORITY_UNAVAILABLE",
] as const;
export type StatementImportErrorCodeV2 = typeof STATEMENT_IMPORT_ERROR_CODES_V2[number];

export interface StatementEvidenceBoundingBoxV2 {
  x: number; y: number; width: number; height: number; unit: "POINT" | "PIXEL";
}
export interface StatementEvidenceLocatorV2 {
  fileId: string;
  row: number | null;
  page: number | null;
  bbox: StatementEvidenceBoundingBoxV2 | null;
  textDigest: string;
}
export interface StatementStagedFieldV2 {
  fieldId: string;
  name: StatementImportFieldNameV2;
  semanticRole: StatementImportSemanticRoleV2;
  valueState: StatementImportValueStateV2;
  rawValue: string | null;
  normalizedValue: string | null;
  extractor: StatementImportExtractorV2;
  confidenceBps: number | null;
  reviewState: StatementImportReviewStateV2;
  reviewedAt: string | null;
  evidence: StatementEvidenceLocatorV2[];
}
export interface StatementAccountIdentityV2 {
  state: "RESOLVED" | "UNKNOWN" | "CONFLICT";
  providerName: string | null;
  maskedAccountRef: string | null;
  currency: string | null;
  sourceTimezone: string | null;
  statementId: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  asOf: string | null;
}
export interface StatementInstrumentIdentityV2 {
  state: "RESOLVED" | "UNKNOWN" | "AMBIGUOUS";
  brokerInstrumentId: string | null;
  isin: string | null;
  cusip: string | null;
  figi: string | null;
  symbol: string | null;
  exchange: string | null;
  currency: string | null;
  resolvedInstrumentId: string | null;
}
export interface StatementEventIdentityV2 {
  strength: "STRONG" | "WEAK" | "MISSING" | "CONFLICT";
  externalEventId: string | null;
  orderId: string | null;
  executionId: string | null;
  canonicalFingerprint: string | null;
}
export interface StatementImportRecordV2 {
  recordId: string;
  kind: StatementImportRecordKindV2;
  semanticRole: StatementImportSemanticRoleV2;
  disposition: StatementImportRecordDispositionV2;
  fields: StatementStagedFieldV2[];
  instrumentIdentity: StatementInstrumentIdentityV2 | null;
  eventIdentity: StatementEventIdentityV2 | null;
  existingTargetIds: string[];
  issueCodes: StatementImportErrorCodeV2[];
}
export interface StatementImportFileV2 {
  fileId: string;
  originalName: string;
  format: StatementImportFormatV2;
  mediaType: string;
  bytes: number;
  sha256: string;
  extractionMode: StatementImportFileExtractionModeV2 | null;
}
export interface StatementImportUploadOperationV2 {
  idempotencyKey: string;
  inputFingerprint: string;
}
export interface StatementImportDispositionCountsV2 {
  NEW: number; BIND_EXISTING: number; SNAPSHOT_ONLY: number;
  POSSIBLE_DUPLICATE: number; CONFLICT: number; IGNORE: number;
}
export interface StatementPositionObservationV2 {
  instrumentId: string;
  quantity: string;
  evidenceIds: string[];
}
export interface StatementReconciliationSideV2 {
  cashBalance: string | null;
  positions: StatementPositionObservationV2[];
}
export interface StatementImportReconciliationV2 {
  state: "NOT_RUN" | "BLOCKED" | "MATCHED" | "CONFLICTED";
  inputFingerprint: string | null;
  current: StatementReconciliationSideV2 | null;
  statement: StatementReconciliationSideV2 | null;
  proposedObservation: StatementReconciliationSideV2 | null;
  dispositionCounts: StatementImportDispositionCountsV2;
  blockingCodes: StatementImportErrorCodeV2[];
  calculatedAt: string | null;
}
export interface PortfolioStatementImportV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  importId: string;
  bookId: string;
  bookKind: PortfolioBookKindV2;
  expectedBookVersion: number;
  reviewRevision: number;
  state: StatementImportStateV2;
  financialAuthority: "NONE";
  uploadOperation: StatementImportUploadOperationV2;
  file: StatementImportFileV2;
  accountIdentity: StatementAccountIdentityV2;
  fields: StatementStagedFieldV2[];
  records: StatementImportRecordV2[];
  reconciliation: StatementImportReconciliationV2;
  createdAt: string;
  updatedAt: string;
}
export interface StatementImportReviewDecisionV2 {
  fieldId: string;
  decision: "CONFIRM" | "CORRECT" | "REJECT";
  correctedValue: string | null;
}
export interface StatementImportReviewInputV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  importId: string;
  expectedRevision: number;
  idempotencyKey: string;
  decisions: StatementImportReviewDecisionV2[];
}
export interface StatementImportReconciliationInputV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  importId: string;
  expectedRevision: number;
  idempotencyKey: string;
}
export interface StatementImportIntentInputV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  importId: string;
  expectedBookVersion: number;
  reconciliationFingerprint: string;
  idempotencyKey: string;
  ownerReviewAcknowledged: true;
  duplicateReviewAcknowledged: true;
}
export interface StatementImportIntentV2 {
  intentId: string;
  importId: string;
  exactBookId: string;
  challengeToken: string;
  expiresAt: string;
  inputFingerprint: string;
  dispositionCounts: StatementImportDispositionCountsV2;
  financialAuthority: "NONE";
  observationsOnlyAcknowledgementRequired: true;
}
export interface StatementImportConfirmInputV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  importId: string;
  intentId: string;
  exactBookId: string;
  challengeToken: string;
  intentFingerprint: string;
  idempotencyKey: string;
  ownerConfirmation: true;
  observationsOnlyAcknowledged: true;
}
export interface StatementImportDiscardInputV2 {
  schemaVersion: PortfolioV2SchemaVersion; importId: string; bookId: string;
  idempotencyKey: string; inputFingerprint: string; ownerConfirmation: true;
}
export interface StatementImportReceiptV2 {
  receiptId: string;
  importId: string;
  bookId: string;
  state: "OBSERVATIONS_ACCEPTED" | "DUPLICATE_NOOP";
  inputFingerprint: string;
  dispositionCounts: StatementImportDispositionCountsV2;
  acceptedObservationIds: string[];
  financialAuthority: "NONE";
  financialWrites: { ledgerTransactions: 0; lots: 0; executions: 0; cashEvents: 0 };
  committedAt: string;
}
export interface StatementImportIntentResponseBindingV2 {
  importId: string; bookId: string; inputFingerprint: string;
  dispositionCounts: StatementImportDispositionCountsV2;
}
export interface StatementImportUploadBindingV2 {
  bookId: string; idempotencyKey: string; inputFingerprint: string;
  fileSha256: string; mediaType: string; bytes: number;
  multipartFileCount: 1; multipartTextFieldCount: 0;
}
export interface StatementImportConfirmResponseBindingV2 {
  importId: string; bookId: string; inputFingerprint: string;
  dispositionCounts: StatementImportDispositionCountsV2;
}
export interface StatementImportStatusResponseBindingV2 {
  operation: StatementImportOperationV2; importId: string | null; bookId: string;
  idempotencyKey: string; inputFingerprint: string;
  dispositionCounts: StatementImportDispositionCountsV2 | null;
}
export type StatementImportOperationV2 = typeof STATEMENT_IMPORT_OPERATIONS_V2[number];
export type StatementImportOperationStateV2 = typeof STATEMENT_IMPORT_OPERATION_STATES_V2[number];
export interface StatementImportOperationStatusV2 {
  operation: StatementImportOperationV2;
  state: StatementImportOperationStateV2;
  importId: string | null;
  bookId: string;
  idempotencyKey: string;
  inputFingerprint: string;
  receipt: StatementImportReceiptV2 | null;
}

export interface StatementImportUploadResponseV2 { ok: true; replayed: boolean; import: PortfolioStatementImportV2 }
export interface StatementImportReadResponseV2 { ok: true; import: PortfolioStatementImportV2 }
export interface StatementImportIntentResponseV2 { ok: true; replayed: boolean; intent: StatementImportIntentV2 }
export interface StatementImportConfirmResponseV2 { ok: true; replayed: boolean; receipt: StatementImportReceiptV2 }
export interface StatementImportStatusResponseV2 { ok: true; status: StatementImportOperationStatusV2 }
